import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  useParseStatement, useImportStatement, useReconcileReport,
  useMarkMatch, useIgnoreTx,
  type StagedRow,
} from "@/lib/statement";
import { formatVnd } from "@/lib/finance";
import { toast } from "sonner";
import { ClipboardPaste, Upload, RefreshCw, Check, X, AlertCircle, FileImage, FileText } from "lucide-react";

export function StatementReconcilePanel() {
  const [text, setText] = useState("");
  const [staged, setStaged] = useState<StagedRow[] | null>(null);
  const [days, setDays] = useState(60);

  const parse = useParseStatement();
  const importMut = useImportStatement();
  const { data: report, refetch, isFetching } = useReconcileReport(days);
  const markMatch = useMarkMatch();
  const ignore = useIgnoreTx();

  async function handleParse() {
    if (!text.trim()) { toast.error("Dán text trước"); return; }
    try {
      const r = await parse.mutateAsync(text);
      setStaged(r.rows);
      if (r.count === 0) toast.warning("Không nhận diện được dòng giao dịch nào");
    } catch (e: any) { toast.error(e.message ?? "Parse lỗi"); }
  }
  async function handleImport() {
    if (!staged || staged.length === 0) { toast.error("Chưa có dòng nào"); return; }
    if (!confirm(`Import ${staged.length} dòng vào staging? Sẽ KHÔNG tạo finance_entries tự động.`)) return;
    try {
      const r = await importMut.mutateAsync({ source: "cake", raw_text: text, rows: staged });
      toast.success(`Đã import ${r.count} dòng vào staging`);
      setStaged(null); setText("");
    } catch (e: any) { toast.error(e.message ?? "Import lỗi"); }
  }

  function txKindLabel(k: string | null) { return k === "expense" ? "Ra" : k === "income" ? "Vào" : "—"; }

  return (
    <div className="space-y-4">
      {/* PASTE FORM */}
      <Card className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <ClipboardPaste className="h-4 w-4" />
          <div className="text-sm font-semibold">Đối soát với statement.cake.vn</div>
          <Badge variant="outline" className="text-[10px]">Paste-text v2</Badge>
        </div>
        <div className="text-xs text-muted-foreground">
          statement.cake.vn không có CSV. Copy & paste text vào ô bên dưới → xem trước → "Lưu vào staging".
          KHÔNG ghi thẳng vào finance_entries. Mọi match/ignore đều cần xác nhận.
        </div>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"Ví dụ:\n14/06/2026  -1.500.000  TT vai do\n14/06/2026  +500.000   Khach Hoa CK"}
          rows={6}
          className="font-mono text-xs"
        />
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleParse} disabled={parse.isPending}>Xem trước</Button>
          <Button size="sm" variant="outline" disabled title="PDF sẽ làm sau">
            <FileText className="h-3.5 w-3.5 mr-1" /> PDF (sắp có)
          </Button>
          <Button size="sm" variant="outline" disabled title="OCR ảnh sẽ làm sau">
            <FileImage className="h-3.5 w-3.5 mr-1" /> Ảnh (sắp có)
          </Button>
        </div>

        {staged && (
          <div className="mt-2 border border-border/40 rounded">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/40 text-xs">
              <span className="font-medium">Xem trước: {staged.length} dòng</span>
              <div className="flex-1" />
              <Button size="sm" onClick={handleImport} disabled={importMut.isPending}>
                <Upload className="h-3.5 w-3.5 mr-1" /> Lưu vào staging
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setStaged(null)}>Huỷ</Button>
            </div>
            <div className="max-h-56 overflow-auto text-xs">
              <table className="w-full">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="text-left px-2 py-1">Ngày</th>
                    <th className="text-left px-2 py-1">Hướng</th>
                    <th className="text-right px-2 py-1">Số tiền</th>
                    <th className="text-left px-2 py-1">Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  {staged.map((r, i) => (
                    <tr key={i} className="border-t border-border/40">
                      <td className="px-2 py-1 whitespace-nowrap">{r.occurred_on ?? "—"}</td>
                      <td className="px-2 py-1">{r.direction === "in" ? "Vào" : "Ra"}</td>
                      <td className="px-2 py-1 text-right font-mono">{formatVnd(r.amount)}</td>
                      <td className="px-2 py-1 truncate max-w-[360px]">{r.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      {/* RECONCILE REPORT */}
      <Card className="p-3 space-y-3">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">So sánh staging ↔ finance_entries</div>
          <Badge variant="outline" className="text-[10px]">{days} ngày gần nhất</Badge>
          <Input type="number" min={7} max={365} value={days} onChange={(e) => setDays(Number(e.target.value) || 60)} className="h-7 w-20 text-xs" />
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isFetching ? "animate-spin" : ""}`} /> Làm mới
          </Button>
        </div>
        {!report ? (
          <div className="text-sm text-muted-foreground">Đang tải…</div>
        ) : report.report.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            Chưa có giao dịch nào trong staging. Hãy paste & import statement.cake.vn trước.
          </div>
        ) : (
          <div className="space-y-2">
            {report.report.map((row) => (
              <div key={row.statement.id} className="border border-border/40 rounded p-2 text-xs space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={
                    row.verdict === "matched" ? "bg-emerald-500/20 text-emerald-500" :
                    row.verdict === "ignored" ? "bg-muted text-muted-foreground" :
                    row.verdict === "missing_in_nana" ? "bg-amber-500/20 text-amber-500" :
                    "bg-blue-500/20 text-blue-500"
                  }>
                    {row.verdict}
                  </Badge>
                  <span className="font-mono">{row.statement.occurred_on ?? "—"}</span>
                  <span>{txKindLabel(row.statement.kind)}</span>
                  <span className="font-mono">{formatVnd(Number(row.statement.amount))}</span>
                  <span className="truncate flex-1 text-muted-foreground">{row.statement.description}</span>
                </div>
                {row.suggestions.length > 0 && (
                  <div className="pl-2 space-y-1">
                    {row.suggestions.map((s) => (
                      <div key={s.finance.id} className="flex items-center gap-2 border border-border/30 rounded px-2 py-1 flex-wrap">
                        <Badge variant="outline" className="text-[10px]">{s.reason}</Badge>
                        <span className="font-mono">{s.finance.occurred_on}</span>
                        <span>{s.finance.kind}</span>
                        <span className="font-mono">{formatVnd(Number(s.finance.amount))}</span>
                        {typeof s.amount_diff === "number" && s.amount_diff > 0 && (
                          <span className="text-amber-500">Δ {formatVnd(s.amount_diff)}</span>
                        )}
                        {typeof s.date_diff_days === "number" && s.date_diff_days > 0 && (
                          <span className="text-amber-500">±{s.date_diff_days}d</span>
                        )}
                        <span className="truncate flex-1 text-muted-foreground">{s.finance.item_name ?? s.finance.note ?? ""}</span>
                        <Button
                          size="sm" variant="outline" className="h-6 text-[11px]"
                          disabled={markMatch.isPending}
                          onClick={() => {
                            if (!confirm(`Khớp giao dịch Cake (${formatVnd(Number(row.statement.amount))}) với finance #${s.finance.id.slice(0,6)}?`)) return;
                            markMatch.mutate({ statement_txn_id: row.statement.id, finance_entry_id: s.finance.id });
                          }}>
                          <Check className="h-3 w-3 mr-1" /> Khớp
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {row.verdict !== "matched" && row.verdict !== "ignored" && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="ghost" className="h-6 text-[11px]"
                      onClick={() => {
                        if (!confirm("Bỏ qua dòng này khỏi đối soát?")) return;
                        ignore.mutate({ statement_txn_id: row.statement.id });
                      }}>
                      <X className="h-3 w-3 mr-1" /> Bỏ qua
                    </Button>
                    {row.suggestions.length === 0 && (
                      <span className="text-amber-500 inline-flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Cake có, Nana thiếu — vào tab "Giao dịch" tạo finance tương ứng (không tự tạo).
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {report && report.finance_orphan.length > 0 && (
          <div>
            <div className="text-sm font-semibold mb-1">Nana có, Cake không có (chưa khớp)</div>
            <div className="max-h-48 overflow-auto text-xs divide-y divide-border/40">
              {report.finance_orphan.map((f) => (
                <div key={f.id} className="py-1 flex items-center gap-2">
                  <span className="font-mono">{f.occurred_on}</span>
                  <Badge variant="outline" className="text-[10px]">{f.kind}</Badge>
                  <span className="font-mono">{formatVnd(Number(f.amount))}</span>
                  <span className="truncate flex-1 text-muted-foreground">{f.item_name ?? f.note ?? ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
